import { Icons } from "@win-reality/react-ui";
import React from "react";
import { useMatches } from "react-router-dom";

import { useAppContext } from "@/hooks/use-app-context";
import { cn } from "@/lib/utils";
import type { RouteHandle } from "@/types/routes";

import { Button } from "../ui/Button";
import BackButton from "./BackButton";
import ContextMenu from "./ContextMenu";

interface NavBarProps {
  className?: string;
  onToggleSidebar?: () => void;
}

/**
 * Redesigned NavBar component.
 * Displays: Back button | Title & Description | Socket Status | Context Menu
 *
 * Title and description are sourced from route handles.
 * Socket status uses React Portal pattern (external components render into container).
 */
const NavBar: React.FC<NavBarProps> = ({ className, onToggleSidebar }) => {
  const { isLoggedIn } = useAppContext();
  const matches = useMatches();

  // Get deepest route match that has a handle defined
  // This handles cases where index routes don't define a handle but their parent layout does
  const currentMatch =
    [...matches].reverse().find((match) => match.handle != null) ??
    matches[matches.length - 1];
  const handle = currentMatch?.handle as RouteHandle | undefined;

  // Get loader data - check current match first, then search parent matches
  // This handles cases where child routes don't have loaders but need parent data
  const loaderData =
    currentMatch?.data ??
    [...matches].reverse().find((match) => match.data !== undefined)?.data;

  // Resolve title (static string or function that receives loader data)
  const title =
    typeof handle?.title === "function"
      ? handle.title(loaderData)
      : (handle?.title ?? "");

  // Resolve description (static string or function)
  const description =
    typeof handle?.description === "function"
      ? handle.description(loaderData)
      : handle?.description;

  return (
    <header
      className={cn(
        "sticky top-0 z-40 w-full h-[var(--navbar-height)] bg-white border-gray-200",
        className,
      )}
    >
      <div className="flex h-full items-center justify-between px-8 md:px-16 gap-4">
        {/* Left section: Mobile menu toggle */}
        {onToggleSidebar && (
          <div className="shrink-0 md:hidden">
            <Button
              variant="ghost"
              className="px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:!size-7"
              onClick={onToggleSidebar}
            >
              <Icons.Bars3BottomLeftOutline className="h-7 w-7" />
              <span className="sr-only">Toggle Menu</span>
            </Button>
          </div>
        )}

        {/* Back button */}
        {isLoggedIn && <BackButton />}

        {/* Center section: Game Header portal OR Title & Description */}
        {/* When the portal has content, the title auto-hides via group-has */}
        <div className="group/header flex flex-1 min-w-0">
          <div
            id="game-header-container"
            className="flex flex-1 items-center justify-center py-1 px-3 empty:hidden min-w-0"
          />
          <div className="group/desc flex flex-col items-start flex-1 min-w-0 group-has-[#game-header-container:not(:empty)]/header:hidden">
            <h1 className="text-lg font-bold text-text-light truncate">
              {title}
            </h1>
            <p
              id="navbar-description-container"
              className="text-md text-text-light truncate empty:hidden"
            />
            {description && (
              <p className="text-md text-text-light truncate group-has-[#navbar-description-container:not(:empty)]/desc:hidden">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Right section: Socket status & Context menu */}
        {isLoggedIn && (
          <div className="flex items-center gap-3 shrink-0 pr-20">
            {/* Socket connection status - rendered via React Portal from other components */}
            <div id="magnus-socket-status-container" />

            {/* Context menu (ellipsis) */}
            <ContextMenu items={handle?.menu} />
          </div>
        )}
      </div>
    </header>
  );
};

export default NavBar;
